import axios, { AxiosError, AxiosResponse } from "axios";
import { Button, Container, Form, Stack, Text } from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { OneTimeCode } from "components/OneTimeCode/OneTimeCode";
import { SvgInline } from "components/SvgInline/SvgInline";
import { components } from "library/api/card-management-service.generated";
import { postHref } from "library/api/httpMethods";
import { Dispatch } from "react";
import { useMutation } from "react-query";
import { getCountryCodePrefix } from "../utils";
import { Actions, ActionTypes } from "./types";

const initialValues = {
  oneTimeCode: "",
};

interface Props {
  dispatch: Dispatch<Actions>;
  mobileNumber: string;
  verifyUrl: string;
  resendUrl: string;
  residentialCountry: string | undefined;
}

export const VerifyMobileNumber = ({
  dispatch,
  mobileNumber,
  verifyUrl,
  resendUrl,
  residentialCountry,
}: Props) => {
  const countryCodePrefix = getCountryCodePrefix(residentialCountry);

  const verifyMutation = useMutation<
    AxiosResponse<components["schemas"]["PhoneVerifyResponse"]>,
    AxiosError<components["schemas"]["ApiError"]>,
    { values: { oneTimeCode: string } }
  >(({ values: { oneTimeCode } }) => postHref(verifyUrl, { otp: oneTimeCode }));

  const onSubmit = async ({ errors, values, setErrors }) => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const response = await verifyMutation.mutateAsync({ values });
      dispatch({
        type: ActionTypes.SUCCESS,
        payload: { phoneOtpResponse: response.data },
      });
    } catch (err) {
      if (!axios.isAxiosError(err)) {
        dispatch({ type: ActionTypes.ERROR });
        return;
      }
      const error = err as AxiosError<components["schemas"]["ApiError"]>;

      if (error.response?.data.type === "2FA_REQUIRED") {
        dispatch({
          type: ActionTypes.VERIFY_OTP_EXPIRED,
          payload: {
            title: "Verification code expired",
            message:
              "We were unable to complete the update of your mobile number. Return to the edit page and we'll send you a new verification code.",
          },
        });
        return;
      }

      const isOtpMissMatch = error.response?.data.failed_params?.find(
        (param) => param.reason === "OTP_MISMATCH",
      );
      if (isOtpMissMatch) {
        setErrors({ oneTimeCode: "invalid code" });
        return;
      }

      const isExceededAllowed = error.response?.data.failed_params?.find(
        (param) => param.reason === "OTP_EXCEEDED_ALLOWED_LIMITS",
      );

      if (isExceededAllowed) {
        dispatch({
          type: ActionTypes.OTP_EXCEEDED_ALLOWED_LIMITS,
          payload: {
            title: "Oops, something went wrong",
            message:
              "We were unable to complete the update of your mobile number. Return to the edit page and we'll send you a new verification code.",
          },
        });
        return;
      }

      dispatch({ type: ActionTypes.ERROR });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      <Stack gap="8">
        <SvgInline name="icon-security-lock" size="92" />
        <Text as="h3" textStyle="heading5" textStyle-sm="heading4">
          Verify your new mobile number
        </Text>
        <OneTimeCode
          otpRecipient={`${countryCodePrefix} ${mobileNumber}`}
          resendMutationFn={() => postHref(resendUrl)}
          onResendCodeError={() => {
            dispatch({ type: ActionTypes.ERROR });
          }}
          hasContactUsLink={false}
        />
        <Container width="100%" width-sm="400">
          <FormActions>
            <FormActions.Primary>
              <Button
                type="submit"
                width="100%"
                loading={verifyMutation.isLoading}
              >
                Verify
              </Button>
            </FormActions.Primary>

            <FormActions.Secondary>
              <Button
                variant="secondary"
                width="100%"
                onClick={() => {
                  dispatch({ type: ActionTypes.BACK });
                }}
              >
                Back
              </Button>
            </FormActions.Secondary>
          </FormActions>
        </Container>
      </Stack>
    </Form>
  );
};
